import type { PreviewSnapshot } from '@/lib/preview/annotation';
import type { PreviewCapturePage } from '@/lib/preview/capture';

export type PreviewAnnotationCaptureRequest = {
  source: 'attune-host';
  type: 'annotation-capture';
  requestId: string;
  rendererSource?: string;
  staticImageUrls?: string[];
  pageIndex?: number;
  capturePage?: boolean;
  scroll?: { x: number; y: number };
};

export type PreviewAnnotationCancelRequest = {
  source: 'attune-host';
  type: 'annotation-cancel';
  requestId: string;
};

export type PreviewAnnotationRenderRequest = {
  source: 'attune-preview-annotation';
  type: 'annotation-render';
  requestId: string;
  html: string;
  viewport: { width: number; height: number; scrollX: number; scrollY: number };
  scrolls: { id: string; x: number; y: number }[];
};

export type PreviewAnnotationCaptureResult = {
  source: 'attune-preview-annotation';
  type: 'annotation-result';
  requestId: string;
} & (
  | { snapshot: PreviewSnapshot; page?: PreviewCapturePage; error?: never }
  | { error: string; snapshot?: never }
);

/** Serialized into both live and compiled frames: keep all runtime values inside this function. */
export function installAttunePreviewAnnotationBridge({
  previewWindow,
  previewProtocol,
  scope,
}: {
  previewWindow: Window;
  previewProtocol: {
    addListener: (
      type: 'annotation-capture',
      listener: (message: PreviewAnnotationCaptureRequest) => void
    ) => void;
    sendMessage: (message: PreviewAnnotationCaptureResult) => void;
  };
  scope: {
    rendererSource?: string;
    nonce?: string;
    isolated?: boolean;
    staticImageUrls?: string[];
  };
}) {
  const frame = previewWindow as Window & {
    html2canvas?: (
      element: HTMLElement,
      options: Record<string, unknown>
    ) => Promise<HTMLCanvasElement>;
    __attunePreviewAnnotationCleanup?: () => void;
  };
  frame.__attunePreviewAnnotationCleanup?.();
  const document = previewWindow.document;
  let active = true;
  let busy = false;
  let rendererScript: HTMLScriptElement | null = null;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let cancelCapture: (() => void) | undefined;
  const overlaySelector =
    '[data-attune-preview-overlay], [data-attune-inspector-overlay], [data-attune-annotation-overlay]';
  const clean = (value: string | null | undefined, max: number) =>
    (value || '')
      .replace(/[\u0000-\u001f\u007f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, max);
  const visible = (element: Element) => {
    const rect = element.getBoundingClientRect();
    if (
      rect.width <= 0 ||
      rect.height <= 0 ||
      rect.bottom <= 0 ||
      rect.right <= 0 ||
      rect.top >= previewWindow.innerHeight ||
      rect.left >= previewWindow.innerWidth
    )
      return false;
    const style = previewWindow.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  };
  const selectorFor = (element: Element) => {
    const parts: string[] = [];
    let current: Element | null = element;
    while (current && current !== document.documentElement && parts.length < 5) {
      if (current.id) {
        parts.unshift(`#${(previewWindow as Window & typeof globalThis).CSS.escape(current.id.slice(0, 80))}`);
        break;
      }
      const parent: Element | null = current.parentElement;
      const tag = current.tagName.toLowerCase();
      const siblings: Element[] = parent
        ? [...parent.children].filter(child => child.tagName === current!.tagName)
        : [];
      parts.unshift(
        siblings.length > 1 ? `${tag}:nth-of-type(${siblings.indexOf(current) + 1})` : tag
      );
      current = parent;
    }
    return parts.join(' > ').slice(0, 240);
  };
  const sendError = (requestId: string, error: string) => {
    if (active)
      previewProtocol.sendMessage({
        source: 'attune-preview-annotation',
        type: 'annotation-result',
        requestId,
        error,
      });
  };
  const capture = async (message: PreviewAnnotationCaptureRequest) => {
    if (
      !active ||
      message.type !== 'annotation-capture' ||
      typeof message.requestId !== 'string' ||
      !message.requestId ||
      message.requestId.length > 160
    )
      return;
    const staticImages = new Set(
      (message.staticImageUrls || scope.staticImageUrls || []).map(href => {
        const url = new URL(href, previewWindow.location.href);
        // The immutable bundle route selects entries by pathname; query and SVG
        // view fragments do not change the inspected resource bytes.
        return url.origin + url.pathname;
      })
    );
    const requestId = message.requestId;
    if (busy) {
      sendError(requestId, 'A preview capture is already running. Try again shortly.');
      return;
    }
    busy = true;
    let captureActive = true;
    const controller = new (previewWindow as Window & typeof globalThis).AbortController();
    const receiveCancellation = (event: MessageEvent) => {
      if (
        event.source === previewWindow.parent &&
        event.data?.source === 'attune-host' &&
        event.data.type === 'annotation-cancel' &&
        event.data.requestId === requestId
      )
        cancelCapture?.();
    };
    previewWindow.addEventListener('message', receiveCancellation);
    const hiddenOverlays: { element: HTMLElement; display: string; priority: string }[] = [];
    const inspectorAttributes: { element: Element; name: string; value: string }[] = [];
    const initialScroll = { x: previewWindow.scrollX, y: previewWindow.scrollY };
    let page: PreviewCapturePage | undefined;
    const positionPage = () => {
      if (message.scroll) {
        if (
          ![message.scroll.x, message.scroll.y].every(
            (value, axis) =>
              Number.isFinite(value) &&
              value >= (axis === 0 ? -100_000_000 : 0) &&
              value <= 100_000_000
          )
        ) {
          throw new Error('The capture scroll position is invalid.');
        }
        previewWindow.scrollTo({
          left: message.scroll.x,
          top: message.scroll.y,
          behavior: 'instant',
        });
      }
      if (message.pageIndex === undefined && !message.capturePage) return;
      const pages = [...document.querySelectorAll('[data-attune-slide]')];
      if (!pages.length && message.pageIndex === undefined) return;
      const pageIndex = message.pageIndex ?? 0;
      if (
        !Number.isSafeInteger(pageIndex) ||
        pageIndex < 0 ||
        pageIndex > 199 ||
        !pages[pageIndex]
      ) {
        throw new Error('The requested Canvas page is unavailable.');
      }
      const rect = pages[pageIndex].getBoundingClientRect();
      previewWindow.scrollTo({
        left: rect.left + previewWindow.scrollX,
        top: rect.top + previewWindow.scrollY,
        behavior: 'instant',
      });
      page = {
        index: pageIndex,
        count: pages.length,
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      };
    };
    const directCapture = previewWindow.origin === 'null';
    const isolatedCopy = directCapture && !scope.isolated;
    const checkSourceBudget = () => {
      if (!isolatedCopy) return;
      // Inspect both the physical and rendered trees without recursion or style
      // reads. Slot assignments can make the rendered depth larger than either
      // the light DOM or a shadow root's depth on its own.
      for (const composed of [false, true]) {
        let count = 0;
        const stack: { nodes: Iterator<Node>; depth: number }[] = [
          { nodes: [document.documentElement][Symbol.iterator](), depth: 0 },
        ];
        while (stack.length) {
          const current = stack[stack.length - 1];
          const next = current.nodes.next();
          if (next.done) {
            stack.pop();
            continue;
          }
          if (++count > 8_000)
            throw new Error('The source view is too large for screenshot capture.');
          if (current.depth > 128)
            throw new Error('The source view is too deep for screenshot capture.');
          const node = next.value;
          const element = node.nodeType === 1 ? (node as Element) : undefined;
          const shadow = element?.shadowRoot;
          const assigned =
            composed && element?.tagName === 'SLOT'
              ? (element as HTMLSlotElement).assignedNodes()
              : [];
          if (!composed && shadow)
            stack.push({ nodes: [shadow][Symbol.iterator](), depth: current.depth + 1 });
          const children =
            composed && shadow ? shadow.childNodes : assigned.length ? assigned : node.childNodes;
          if (children.length)
            stack.push({ nodes: children[Symbol.iterator](), depth: current.depth + 1 });
        }
      }
    };
    try {
      checkSourceBudget();
      if (!isolatedCopy && !frame.html2canvas) {
        const source = scope.rendererSource || message.rendererSource;
        if (typeof source !== 'string' || !source || source.length > 1_000_000)
          throw new Error('The screenshot renderer is unavailable. Reload the preview and retry.');
        if (scope.nonce) {
          // Compiled opaque previews authorize this nonce and prohibit eval.
          rendererScript = document.createElement('script');
          rendererScript.nonce = scope.nonce;
          rendererScript.textContent = source;
          document.head.appendChild(rendererScript);
        } else {
          // The live template already permits SDK code injection via unsafe-eval,
          // but deliberately blocks arbitrary inline scripts. The frame's own
          // constructor executes the local renderer under that unchanged CSP.
          new (previewWindow as Window & typeof globalThis).Function(source).call(previewWindow);
        }
        if (typeof frame.html2canvas !== 'function')
          throw new Error(
            'The preview blocked the screenshot renderer. Reload the preview and retry.'
          );
      }
      positionPage();
      const viewport = {
        width: previewWindow.innerWidth,
        height: previewWindow.innerHeight,
        scrollX: previewWindow.scrollX,
        scrollY: previewWindow.scrollY,
      };
      for (const name of isolatedCopy
        ? []
        : ['data-attune-preview-hover', 'data-attune-preview-selected']) {
        for (const element of document.querySelectorAll(`[${name}]`)) {
          inspectorAttributes.push({ element, name, value: element.getAttribute(name) || '' });
          element.removeAttribute(name);
        }
      }
      if (
        !document.body ||
        viewport.width < 1 ||
        viewport.height < 1 ||
        viewport.width > 16_384 ||
        viewport.height > 16_384
      )
        throw new Error('The preview viewport cannot be captured.');
      // A renderer cannot faithfully paint nested browsing contexts or media controls.
      const visibleMedia = (element: Element) => {
        if (!visible(element)) return false;
        let ancestor: Element | null = element;
        while (ancestor) {
          const style = previewWindow.getComputedStyle(ancestor);
          if (style.opacity === '0' || style.display === 'none') return false;
          ancestor =
            ancestor.assignedSlot ||
            ancestor.parentElement ||
            (ancestor.getRootNode() as ShadowRoot).host ||
            null;
        }
        return true;
      };
      if ([...document.querySelectorAll('iframe,video,audio,object,embed')].some(visibleMedia))
        throw new Error(
          'This view contains media or embedded content that cannot be captured. Scroll to the area you want to annotate and retry.'
        );
      if (directCapture && !isolatedCopy) {
        for (const element of document.querySelectorAll<HTMLElement>(overlaySelector)) {
          hiddenOverlays.push({
            element,
            display: element.style.getPropertyValue('display'),
            priority: element.style.getPropertyPriority('display'),
          });
          element.style.setProperty('display', 'none', 'important');
        }
      }
      const collectElements = () =>
        [...document.querySelectorAll('body *')]
          .slice(0, 8_000)
          .filter(
            element =>
              !element.closest(overlaySelector) &&
              !['SCRIPT', 'STYLE', 'LINK', 'META', 'NOSCRIPT'].includes(element.tagName) &&
              visible(element)
          )
          .sort(
            (a, b) =>
              Number(b.matches('[data-attune-node-id],h1,h2,h3,button,a,img,input,label')) -
              Number(a.matches('[data-attune-node-id],h1,h2,h3,button,a,img,input,label'))
          )
          .slice(0, 60)
          .map(element => {
            const rect = element.getBoundingClientRect();
            const nodeId = clean(
              element.closest('[data-attune-node-id]')?.getAttribute('data-attune-node-id'),
              200
            );
            return {
              selector: selectorFor(element),
              ...(nodeId ? { nodeId } : {}),
              tag: element.tagName.toLowerCase(),
              label: clean(
                element.getAttribute('aria-label') ||
                  element.getAttribute('alt') ||
                  element.getAttribute('title') ||
                  element.textContent,
                160
              ),
              bounds: {
                x: Math.round(rect.x),
                y: Math.round(rect.y),
                width: Math.round(rect.width),
                height: Math.round(rect.height),
              },
            };
          });
      const imageUrls = new Set<string>();
      type ImageSize = {
        width: number;
        height: number;
        contain?: boolean;
        stretch?: boolean;
        pixelated?: boolean;
      };
      const imageSizes = new Map<string, (ImageSize | undefined)[]>();
      const captureScale = Math.min(1, 1_600 / Math.max(viewport.width, viewport.height));
      const addImageUrl = (url: string, size?: ImageSize) => {
        const absolute = new URL(url, document.baseURI || previewWindow.location.href).href;
        imageUrls.add(absolute);
        const sizes = imageSizes.get(absolute) || [];
        sizes.push(
          size
            ? {
                width: size.width * captureScale,
                height: size.height * captureScale,
                contain: size.contain,
                stretch: size.stretch,
                pixelated: size.pixelated,
              }
            : undefined
        );
        imageSizes.set(absolute, sizes);
      };
      const decodeCssValue = (value: string) =>
        value.replace(
          /\\(?:([0-9a-f]{1,6})(?:\r\n|[ \t\r\n\f])?|(\r\n|[\r\n\f])|(.))/gi,
          (
            _match,
            hex: string | undefined,
            newline: string | undefined,
            escaped: string | undefined
          ) => {
            if (!hex) return newline ? '' : escaped || '';
            const code = Number.parseInt(hex, 16);
            return String.fromCodePoint(
              code === 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff) ? 0xfffd : code
            );
          }
        );
      const replaceCssUrls = (
        resources: string,
        replace: (url: string, token: string) => string
      ) => {
        // Consume strings too: text such as "url(example)" is not an image.
        const tokens =
          /url\(\s*(?:"((?:\\.|[^"\\])*)"|'((?:\\.|[^'\\])*)'|((?:\\.|[^)\\])*))\s*\)|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/gi;
        return resources.replace(
          tokens,
          (
            token,
            double: string | undefined,
            single: string | undefined,
            unquoted: string | undefined
          ) => {
            const value = double ?? single ?? unquoted?.trim();
            if (value === undefined) return token;
            return replace(decodeCssValue(value), token);
          }
        );
      };
      const addCssImageUrls = (resources: string, size?: ImageSize) => {
        replaceCssUrls(resources, (url, token) => {
          if (url.trim()) addImageUrl(url, size);
          return token;
        });
      };
      const splitCssValues = (value: string, separator: ',' | ' ') => {
        const parts: string[] = [];
        let start = 0;
        let depth = 0;
        let quote = '';
        for (let i = 0; i < value.length; i++) {
          const char = value[i];
          if (char === '\\') {
            i++;
            continue;
          }
          if (quote) {
            if (char === quote) quote = '';
            continue;
          }
          if (char === '"' || char === "'") {
            quote = char;
            continue;
          }
          if (char === '(') depth++;
          else if (char === ')') depth--;
          else if (depth === 0 && (separator === ',' ? char === ',' : /\s/.test(char))) {
            if (value.slice(start, i).trim()) parts.push(value.slice(start, i).trim());
            start = i + 1;
          }
        }
        if (value.slice(start).trim()) parts.push(value.slice(start).trim());
        return parts;
      };
      const backgroundSizeHint = (value: string, bounds: DOMRect) => {
        if (value === 'cover' || value === 'contain')
          return { width: bounds.width, height: bounds.height, contain: value === 'contain' };
        const [x = 'auto', y = 'auto'] = splitCssValues(value, ' ');
        const length = (component: string, basis: number) => {
          if (component === 'auto') return 0;
          // Computed lengths use px/%; sum absolute terms for a conservative
          // bound on calc/min/max/clamp without evaluating author expressions.
          const terms = [...component.matchAll(/(-?\d*\.?\d+(?:e[+-]?\d+)?)(px|%)/gi)];
          if (!terms.length) return component === '0' ? 0 : undefined;
          return terms.reduce(
            (sum, term) => sum + Math.abs(Number(term[1])) * (term[2] === '%' ? basis / 100 : 1),
            0
          );
        };
        const width = length(x, bounds.width);
        const height = length(y, bounds.height);
        return width !== undefined && height !== undefined && (width > 0 || height > 0)
          ? { width, height, stretch: x !== 'auto' && y !== 'auto' }
          : undefined;
      };
      const hasBoundedObjectFit = (
        element: HTMLElement,
        style: CSSStyleDeclaration,
        width: number,
        height: number
      ) => {
        if (['fill', 'contain', 'cover'].includes(style.objectFit || 'fill')) return true;
        if (style.objectFit !== 'scale-down') return false;
        const bounds = element.getBoundingClientRect();
        const contentWidth =
          (element.clientWidth ?? bounds.width) -
          (Number.parseFloat(style.paddingLeft) || 0) -
          (Number.parseFloat(style.paddingRight) || 0);
        const contentHeight =
          (element.clientHeight ?? bounds.height) -
          (Number.parseFloat(style.paddingTop) || 0) -
          (Number.parseFloat(style.paddingBottom) || 0);
        // scale-down resolves to contain only when the natural image is larger
        // than its content box. Otherwise its intrinsic dimensions govern layout.
        return width > contentWidth || height > contentHeight;
      };
      const addElementImage = (image: HTMLImageElement, style: CSSStyleDeclaration) => {
        if (!visible(image)) return;
        if (!image.complete || image.naturalWidth === 0)
          throw new Error(
            'Preview images are still loading or unavailable. Wait for them to load and retry.'
          );
        addImageUrl(
          image.currentSrc || image.src,
          hasBoundedObjectFit(image, style, image.naturalWidth, image.naturalHeight)
            ? {
                width: image.getBoundingClientRect().width,
                height: image.getBoundingClientRect().height,
                contain: ['contain', 'scale-down'].includes(style.objectFit),
                stretch: !style.objectFit || style.objectFit === 'fill',
                pixelated: ['pixelated', 'crisp-edges'].includes(style.imageRendering),
              }
            : undefined
        );
      };
      const svgDefinitions = 'defs,symbol,pattern,mask,clipPath,marker,filter';
      let referencedSvgImages = new WeakMap<Node, Set<Element>>();
      const svgImageMayPaint = (element: SVGImageElement | SVGFEImageElement) => {
        if (!element.closest(svgDefinitions)) return visible(element);
        const root = element.getRootNode() as Document | ShadowRoot;
        let images = referencedSvgImages.get(root);
        if (!images) {
          images = new Set<Element>();
          referencedSvgImages.set(root, images);
          const seen = new Set<Element>();
          const reference = (href: string) => {
            if (!href) return;
            const url = new URL(href, document.baseURI);
            if (!url.hash || url.href.split('#')[0] !== document.baseURI.split('#')[0]) return;
            let id = url.hash.slice(1);
            try {
              id = decodeURIComponent(id);
            } catch {
              /* Keep literal IDs with malformed escapes. */
            }
            const target = root.getElementById(id);
            if (target?.namespaceURI === 'http://www.w3.org/2000/svg') visit(target, true);
          };
          const visit = (node: Element, descendants: boolean) => {
            if (seen.has(node)) return;
            seen.add(node);
            const style = previewWindow.getComputedStyle(node);
            if (style.display === 'none' || style.opacity === '0') return;
            if (['image', 'feImage'].includes(node.localName) && style.visibility !== 'hidden')
              images!.add(node);
            reference(
              node.getAttribute('href') ||
                node.getAttributeNS('http://www.w3.org/1999/xlink', 'href') ||
                ''
            );
            for (const property of [
              'fill',
              'stroke',
              'filter',
              'clip-path',
              'mask-image',
              'marker-start',
              'marker-mid',
              'marker-end',
            ])
              replaceCssUrls(style.getPropertyValue(property), (url, token) => {
                reference(url);
                return token;
              });
            if (descendants)
              for (const child of node.children)
                if (!child.matches(svgDefinitions)) visit(child, true);
          };
          // Start at painted consumers, then follow local href/CSS references
          // transitively. Merely sharing a visible SVG does not paint a definition.
          for (const node of root.querySelectorAll('svg,svg *'))
            if (!node.closest(svgDefinitions) && visible(node)) visit(node, false);
        }
        return images.has(element);
      };
      // Resource checks follow the rendered tree, including shadow hosts and
      // assigned slots. Host bounds and the element-context cap cannot exclude
      // a pseudo that extends into the viewport from a zero-sized/offscreen box.
      const collectImageUrls = (element: Element) => {
        if (
          element.closest(overlaySelector) ||
          ['SCRIPT', 'STYLE', 'LINK', 'META'].includes(element.tagName)
        )
          return;
        const hostStyle = previewWindow.getComputedStyle(element);
        if (hostStyle.display === 'none' || hostStyle.opacity === '0') return;
        if (
          visible(element) &&
          ['INPUT', 'TEXTAREA'].includes(element.tagName) &&
          element.matches(':placeholder-shown')
        ) {
          const placeholder = previewWindow.getComputedStyle(element, '::placeholder');
          if (
            element.tagName === 'TEXTAREA' ||
            placeholder.color !== 'rgb(117, 117, 117)' ||
            placeholder.opacity !== '1' ||
            [
              'font-family',
              'font-size',
              'font-weight',
              'font-style',
              'letter-spacing',
              'text-transform',
              'text-shadow',
            ].some(
              property =>
                placeholder.getPropertyValue(property) !== hostStyle.getPropertyValue(property)
            )
          )
            throw new Error('This view uses placeholder styling that cannot be captured.');
        }
        if (element.tagName === 'IMG') addElementImage(element as HTMLImageElement, hostStyle);
        if (
          element.namespaceURI === 'http://www.w3.org/2000/svg' &&
          ['image', 'feImage'].includes(element.localName) &&
          svgImageMayPaint(element as SVGImageElement)
        ) {
          const href = (element as SVGImageElement).href.baseVal;
          if (href)
            addImageUrl(
              href,
              visible(element)
                ? {
                    width: element.getBoundingClientRect().width,
                    height: element.getBoundingClientRect().height,
                    contain:
                      (element as SVGImageElement).preserveAspectRatio.baseVal.align !== 1 &&
                      (element as SVGImageElement).preserveAspectRatio.baseVal.meetOrSlice !== 2,
                    stretch: (element as SVGImageElement).preserveAspectRatio.baseVal.align === 1,
                    pixelated: ['pixelated', 'crisp-edges'].includes(hostStyle.imageRendering),
                  }
                : undefined
            );
        }
        for (const pseudo of [null, '::before', '::after']) {
          const style = pseudo ? previewWindow.getComputedStyle(element, pseudo) : hostStyle;
          if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0')
            continue;
          if (pseudo && (!style.content || style.content === 'none' || style.content === 'normal'))
            continue;
          // Native pseudos and list markers can paint outside the host box.
          // Ordinary images/backgrounds outside this viewport are irrelevant.
          if (pseudo || visible(element)) {
            if (
              pseudo &&
              ['backdrop-filter', '-webkit-backdrop-filter'].some(property => {
                const value = style.getPropertyValue(property);
                return value && value !== 'none';
              })
            )
              throw new Error(
                'This view uses a generated backdrop filter that cannot be captured.'
              );
            if (element.namespaceURI === 'http://www.w3.org/2000/svg') {
              for (const property of [
                'fill',
                'stroke',
                'clip-path',
                'marker-start',
                'marker-mid',
                'marker-end',
              ])
                replaceCssUrls(style.getPropertyValue(property), (href, token) => {
                  const reference = new URL(href, document.baseURI);
                  if (
                    !reference.hash ||
                    reference.href.split('#')[0] !== document.baseURI.split('#')[0]
                  )
                    throw new Error(
                      'This view uses an external SVG paint server that cannot be captured.'
                    );
                  return token;
                });
            }
            replaceCssUrls(style.filter || '', (href, token) => {
              const reference = new URL(href, document.baseURI);
              if (
                !reference.hash ||
                reference.href.split('#')[0] !== document.baseURI.split('#')[0]
              )
                throw new Error('This view uses an external SVG filter that cannot be captured.');
              if (pseudo || element.namespaceURI !== 'http://www.w3.org/2000/svg')
                throw new Error(
                  'This view uses an SVG filter on HTML content that cannot be captured.'
                );
              return token;
            });
            // The renderer has no CSS mask paint path. Embedding its images
            // alone would still produce a successful, unmasked screenshot.
            if (
              [style.maskImage, style.webkitMaskImage].some(mask =>
                splitCssValues(mask || 'none', ',').some(layer => layer.trim() !== 'none')
              )
            )
              throw new Error('This view uses a CSS mask that cannot be captured.');
            const sizes = splitCssValues(style.backgroundSize || 'auto', ',');
            for (const [index, layer] of splitCssValues(
              style.backgroundImage || '',
              ','
            ).entries()) {
              if (/^(?:-webkit-)?image-set\(/i.test(layer.trim()))
                throw new Error('This view uses an image-set background that cannot be captured.');
              const hint = !pseudo
                ? backgroundSizeHint(sizes[index % sizes.length], element.getBoundingClientRect())
                : undefined;
              addCssImageUrls(
                layer,
                hint
                  ? {
                      ...hint,
                      pixelated: ['pixelated', 'crisp-edges'].includes(style.imageRendering),
                    }
                  : undefined
              );
            }
          }
          // The vendored border renderer does not implement outsets, which can
          // extend into the viewport even when the host itself is outside it.
          if (
            style.borderImageSource &&
            style.borderImageSource !== 'none' &&
            (style.borderImageOutset || '0')
              .split(/\s+/)
              .some(value => Number.parseFloat(value) !== 0)
          )
            throw new Error('This border image uses an outset that cannot be captured accurately.');
          if (pseudo || visible(element)) addCssImageUrls(style.borderImageSource || '');
          if (style.cssFloat !== 'none') addCssImageUrls(style.shapeOutside || '');
          if (pseudo || visible(element) || hostStyle.display.includes('list-item'))
            addCssImageUrls(style.listStyleImage || '');
          if (pseudo) addCssImageUrls(style.content || '');
        }
        const assigned =
          element.tagName === 'SLOT' ? (element as HTMLSlotElement).assignedNodes() : [];
        const children = assigned.length
          ? assigned.filter((node): node is Element => node.nodeType === 1)
          : (element.shadowRoot || element).children;
        for (const child of children) collectImageUrls(child);
      };
      const staticEmbeddedRaster = (binary: string) => {
        const uint32 = (offset: number, little = false) => {
          const bytes = [0, 1, 2, 3].map(index => binary.charCodeAt(offset + index));
          if (little) bytes.reverse();
          return bytes.reduce((value, byte) => value * 256 + byte, 0);
        };
        // AVIF sequences have an avis brand and/or a movie timeline. Inspect
        // top-level boxes only; static AVIF pixels remain supported.
        if (binary.slice(4, 8) === 'ftyp') {
          const end = uint32(0);
          if (end < 16 || end > binary.length || end % 4) return false;
          let avif = false;
          for (let offset = 8; offset < end; offset += 4) {
            if (offset === 12) continue; // minor_version is not a brand
            const brand = binary.slice(offset, offset + 4);
            if (brand === 'avis' || brand === 'msf1') return false;
            if (brand === 'avif') avif = true;
          }
          if (!avif) return false;
          let image = false;
          for (let offset = end; offset < binary.length; ) {
            if (offset + 8 > binary.length) return false;
            const length = uint32(offset) || binary.length - offset;
            const type = binary.slice(offset + 4, offset + 8);
            if (length < 8 || offset + length > binary.length || type === 'moov') return false;
            if (type === 'mdat') image = true;
            offset += length;
          }
          return image;
        }
        if (binary.slice(0, 8) === '\x89PNG\r\n\x1a\n') {
          for (let offset = 8; offset + 12 <= binary.length; ) {
            const length = uint32(offset);
            const type = binary.slice(offset + 4, offset + 8);
            if (offset + 12 + length > binary.length || type === 'acTL') return false;
            if (type === 'IEND') return true;
            offset += 12 + length;
          }
          return false;
        }
        if (binary.startsWith('RIFF') && binary.slice(8, 12) === 'WEBP') {
          const end = uint32(4, true) + 8;
          if (end > binary.length || end < 12) return false;
          let image = false;
          let offset = 12;
          for (; offset + 8 <= end; ) {
            const length = uint32(offset + 4, true);
            const type = binary.slice(offset, offset + 4);
            if (offset + 8 + length > end || type === 'ANIM' || type === 'ANMF') return false;
            if (type === 'VP8X' && (!length || binary.charCodeAt(offset + 8) & 2)) return false;
            if (type === 'VP8 ' || type === 'VP8L') image = true;
            offset += 8 + length + (length % 2);
          }
          return image && offset === end;
        }
        return false;
      };
      const staticNestedImage = (url: string) => {
        try {
          const resource = url.split('#', 1)[0];
          if (!resource.startsWith('data:')) return false;
          const comma = resource.indexOf(',');
          if (comma < 0) return false;
          const header = resource.slice(0, comma);
          const decoded = resource
            .slice(comma + 1)
            .replace(/%([0-9a-f]{2})/gi, (_match, hex: string) =>
              String.fromCharCode(Number.parseInt(hex, 16))
            );
          const binary = /;base64$/i.test(header) ? previewWindow.atob(decoded) : decoded;
          return binary.startsWith('\xff\xd8\xff') || staticEmbeddedRaster(binary);
        } catch {
          return false;
        }
      };
      const scalableImages = new Set<string>();
      const preloadedImages = new Map<string, HTMLImageElement>();
      const collectResources = () => {
        checkSourceBudget();
        imageUrls.clear();
        imageSizes.clear();
        referencedSvgImages = new WeakMap();
        collectImageUrls(document.documentElement);
      };
      // Fonts have a bounded wait within the capture timeout, as do CORS image checks.
      const work = async () => {
        await document.fonts?.ready;
        if (!captureActive || !active) throw new Error('Preview capture was cancelled.');
        // Font layout settles before deciding which ordinary images can paint
        // into this viewport. Shadow/slotted images are collected recursively.
        collectResources();
        for (const imageUrl of imageUrls) {
          if (!captureActive || !active) throw new Error('Preview capture was cancelled.');
          const url = new URL(imageUrl, previewWindow.location.href);
          if (
            !directCapture &&
            (url.protocol === 'data:' ||
              url.protocol === 'blob:' ||
              url.origin === previewWindow.location.origin)
          )
            continue;
          await new Promise<void>((resolve, reject) => {
            const probe = new (previewWindow as Window & typeof globalThis).Image();
            probe.crossOrigin = 'anonymous';
            probe.onload = () => {
              preloadedImages.set(url.href, probe);
              resolve();
            };
            probe.onerror = () =>
              reject(
                new Error(
                  'An image in this view does not permit screenshot capture. Choose another view or retry after it loads.'
                )
              );
            probe.src = url.href;
          });
          // Canvas draws a GIF's default frame, including when given the live
          // image element. CORS resource metadata identifies extensionless GIF
          // URLs without fetching bytes through the preview's connect-src policy.
          const resourceTypes =
            previewWindow.performance
              ?.getEntriesByName(url.href, 'resource')
              .map(
                entry => (entry as PerformanceResourceTiming & { contentType?: string }).contentType
              ) || [];
          const declaredType = url.href.match(/^data:([^;,]*)/i)?.[1];
          if (
            [...resourceTypes, declaredType].some(type => type?.toLowerCase() === 'image/gif') ||
            (url.protocol !== 'data:' && /\.gif$/i.test(url.pathname))
          )
            throw new Error('This view uses a GIF image whose displayed frame cannot be captured.');
          const animationError = () =>
            new Error('The animation state of an image in this view cannot be captured.');
          if (url.protocol === 'blob:') throw animationError();
          if (url.protocol === 'data:') {
            // Fragments select SVG views but are not part of the encoded bytes.
            // Keep the original URL on the loaded image used for rasterization.
            const resource = url.href.split('#', 1)[0];
            const comma = resource.indexOf(',');
            const header = resource.slice(0, comma);
            const payload = resource.slice(comma + 1);
            const decodePercentBytes = (value: string) =>
              value.replace(/%([0-9a-f]{2})/gi, (_match, hex: string) =>
                String.fromCharCode(Number.parseInt(hex, 16))
              );
            const base64 = /;base64$/i.test(header);
            const prefix = base64
              ? previewWindow.atob(decodePercentBytes(payload.slice(0, 48)).slice(0, 16))
              : decodePercentBytes(payload.slice(0, 48));
            if (prefix.startsWith('GIF8')) throw animationError();
            if (/image\/svg\+xml/i.test(declaredType || '') || /^\s*</.test(prefix)) {
              if (url.href.length > 16_000_000) throw animationError();
              const decoded = decodePercentBytes(payload);
              const xml = base64 ? previewWindow.atob(decoded) : decoded;
              // Bound native XML work before constructing any SVG DOM. Literal
              // markup delimiters conservatively include comments and CDATA.
              if (xml.length > 2_000_000 || /<!DOCTYPE|<!ENTITY/i.test(xml))
                throw new Error('An SVG image is too complex for screenshot capture.');
              for (const [delimiter, limit] of [
                ['<', 8_000],
                ['{', 4_000],
              ] as const) {
                for (let offset = 0, markers = 0; ; markers++) {
                  offset = xml.indexOf(delimiter, offset);
                  if (offset === -1) break;
                  if (markers >= limit)
                    throw new Error('An SVG image is too complex for screenshot capture.');
                  offset++;
                }
              }
              const svg = new (previewWindow as Window & typeof globalThis).DOMParser().parseFromString(xml, 'image/svg+xml');
              if (
                svg.querySelector(
                  'parsererror,animate,set,animateTransform,animateMotion,animateColor,discard,script'
                )
              )
                throw animationError();
              if (
                [...svg.querySelectorAll('image,feImage')].some(image => {
                  const href =
                    image.getAttribute('href') ||
                    image.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
                  return href && !staticNestedImage(href);
                })
              )
                throw animationError();
              scalableImages.add(url.href);
              const styles = [
                ...[...svg.querySelectorAll('style')].map(style => style.textContent || ''),
                ...[...svg.querySelectorAll('[style]')].map(
                  node => `x{${node.getAttribute('style')}}`
                ),
              ];
              for (const css of styles) {
                if (css.length > 1_000_000) throw animationError();
                const sheet = new (previewWindow as Window & typeof globalThis).CSSStyleSheet();
                sheet.replaceSync(css);
                const pending = [...sheet.cssRules];
                while (pending.length) {
                  const rule = pending.pop()!;
                  if (rule.type === 7 || rule.type === 3) throw animationError();
                  const declaration = (rule as CSSStyleRule).style;
                  if (
                    declaration &&
                    [...declaration].some(property =>
                      /^(?:-webkit-)?animation(?:-|$)/.test(property)
                    )
                  )
                    throw animationError();
                  if ('cssRules' in rule) pending.push(...(rule as CSSGroupingRule).cssRules);
                }
              }
            }
            if (
              /image\/(?:png|apng|webp|avif)/i.test(declaredType || '') ||
              prefix.startsWith('\x89PNG') ||
              prefix.slice(4, 8) === 'ftyp' ||
              (prefix.startsWith('RIFF') && prefix.slice(8, 12) === 'WEBP')
            ) {
              if (url.href.length > 16_000_000) throw animationError();
              const decoded = decodePercentBytes(payload);
              const binary = base64 ? previewWindow.atob(decoded) : decoded;
              if (!staticEmbeddedRaster(binary)) throw animationError();
            }
          } else {
            const responseType = resourceTypes.filter(Boolean).at(-1)?.toLowerCase();
            if (responseType === 'image/svg+xml' || (!responseType && /\.svg$/i.test(url.pathname)))
              scalableImages.add(url.href);
            // Remote PNG/WebP/AVIF bytes cannot be read under connect-src 'none'.
            // Their MIME does not distinguish an animation from a static image.
            if (
              !staticImages.has(url.origin + url.pathname) &&
              (/^image\/(?:png|apng|webp|avif|svg\+xml)$/.test(responseType || '') ||
                (!responseType && /\.(?:png|apng|webp|avif|svg)$/i.test(url.pathname)))
            )
              throw animationError();
          }
        }
        if (!captureActive || !active) throw new Error('Preview capture was cancelled.');
        if (message.pageIndex !== undefined || message.capturePage || message.scroll) {
          positionPage();
          viewport.scrollX = previewWindow.scrollX;
          viewport.scrollY = previewWindow.scrollY;
        }
        if (isolatedCopy) {
          // Resource loading can reflow the source. Never replace a newly visible
          // image with an offscreen placeholder or use stale raster dimensions.
          collectResources();
          if ([...imageUrls].some(url => !preloadedImages.has(url)))
            throw new Error(
              'Preview images changed during capture. Keep the view still and retry.'
            );
        }
        // Font loading and CORS probes can reflow the page. Collect geometry at
        // the same boundary at which the renderer snapshots the document.
        const elements = collectElements();
        const capturedAt = new Date().toISOString();
        if (isolatedCopy) {
          // Build in an inert document: importing custom elements here cannot
          // upgrade them, and no writes are made to the observed preview tree.
          const copy = document.implementation.createHTMLDocument('Preview capture');
          const imageData = new Map<string, string>();
          let encodedImageBytes = 0;
          const emptyImage =
            'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/%3E';
          const rasterizedImage = (
            source: CanvasImageSource,
            sourceWidth: number,
            sourceHeight: number,
            sizes?: (ImageSize | undefined)[],
            scalable = false
          ) => {
            if (!sourceWidth || !sourceHeight) return emptyImage;
            const scale = !sizes?.length
              ? 1
              : Math.min(
                  scalable ? Infinity : 1,
                  sizes.reduce(
                    (largest, size) =>
                      Math.max(
                        largest,
                        !size
                          ? 1
                          : (size.contain ? Math.min : Math.max)(
                              size.width / sourceWidth,
                              size.height / sourceHeight
                            )
                      ),
                    0
                  )
                );
            // Stretch-only consumers do not depend on the encoded aspect ratio.
            // A shared contain/cover/auto use still needs that ratio preserved.
            const stretch = sizes?.length && sizes.every(size => size?.stretch);
            const width = Math.max(
              1,
              Math.ceil(
                stretch
                  ? Math.min(
                      scalable ? Infinity : sourceWidth,
                      sizes.reduce((largest, size) => Math.max(largest, size!.width), 0)
                    )
                  : sourceWidth * scale
              )
            );
            const height = Math.max(
              1,
              Math.ceil(
                stretch
                  ? Math.min(
                      scalable ? Infinity : sourceHeight,
                      sizes.reduce((largest, size) => Math.max(largest, size!.height), 0)
                    )
                  : sourceHeight * scale
              )
            );
            if (
              (width !== sourceWidth || height !== sourceHeight) &&
              sizes?.some(size => size?.pixelated)
            )
              throw new Error(
                'This view uses a pixelated image that cannot be downsampled for capture.'
              );
            if (width > 16_384 || height > 16_384 || width * height > 16_000_000)
              throw new Error('An image needs too much pixel memory for screenshot capture.');
            const canvas = document.createElement('canvas');
            try {
              canvas.width = width;
              canvas.height = height;
              canvas.getContext('2d')!.drawImage(source, 0, 0, width, height);
              const data = canvas.toDataURL();
              if (data.length > 8_000_000)
                throw new Error('An image is too detailed for screenshot capture.');
              encodedImageBytes += data.length;
              if (encodedImageBytes > 16_000_000)
                throw new Error(
                  'Images in this view need too much encoded memory for screenshot capture.'
                );
              return data;
            } finally {
              canvas.width = canvas.height = 0;
            }
          };
          const capturedUrl = (url: string) => {
            if (url.startsWith('#')) return url;
            const localReference = document.baseURI.split('#')[0] + '#';
            if (url.startsWith(localReference)) return url.slice(localReference.length - 1);
            const absolute = new URL(url, document.baseURI).href;
            if (imageData.has(absolute)) return imageData.get(absolute)!;
            const loaded = preloadedImages.get(absolute);
            if (!loaded) return emptyImage;
            const data = rasterizedImage(
              loaded,
              loaded.naturalWidth,
              loaded.naturalHeight,
              imageSizes.get(absolute),
              scalableImages.has(absolute)
            );
            imageData.set(absolute, data);
            return data;
          };
          const copiedCss = (value: string) =>
            replaceCssUrls(value, url => `url(${JSON.stringify(capturedUrl(url))})`);
          const scrolls: PreviewAnnotationRenderRequest['scrolls'] = [];
          let nextId = 0;
          const fontRules: string[] = [];
          const fontFaceRules: { family: string; css: string }[] = [];
          const copiedFonts = new Set<string>();
          const referencedFontFamilies = new Set<string>();
          const normalizedFontFamily = (family: string) =>
            decodeCssValue(/^['"]/.test(family) ? family.slice(1, -1) : family).toLowerCase();
          const noteFontFamilies = (style: CSSStyleDeclaration) => {
            for (const family of splitCssValues(style.fontFamily || '', ','))
              referencedFontFamilies.add(normalizedFontFamily(family));
          };
          const fontKey = (font: FontFace, family = font.family) =>
            JSON.stringify([
              family,
              font.style,
              font.weight,
              font.stretch,
              font.unicodeRange,
              font.featureSettings,
              (font as FontFace & { variationSettings?: string }).variationSettings,
            ]);
          const seenSheets = new Set<CSSStyleSheet>();
          const collectFonts = (sheet: CSSStyleSheet) => {
            if (seenSheets.has(sheet)) return;
            seenSheets.add(sheet);
            if (
              sheet.disabled ||
              (sheet.media?.mediaText && !previewWindow.matchMedia(sheet.media.mediaText).matches)
            )
              return;
            let rules: CSSRuleList;
            try {
              rules = sheet.cssRules;
            } catch {
              // Cross-origin sheets still contribute computed styles. Loaded
              // font faces are checked separately so missing font bytes cannot
              // silently change text metrics in the copy.
              return;
            }
            const visitRules = (rules: CSSRuleList) => {
              for (const rule of rules) {
                // Flatten only active conditional groups. Promoting an inactive
                // face to the copy would replace a source fallback font.
                if (
                  rule.type === 4 &&
                  !previewWindow.matchMedia((rule as CSSMediaRule).conditionText).matches
                )
                  continue;
                if (
                  rule.type === 12 &&
                  !(previewWindow as Window & typeof globalThis).CSS.supports((rule as CSSSupportsRule).conditionText)
                )
                  continue;
                if (rule.type === 11) {
                  // Preserve only custom counter names, including overrides of
                  // built-ins, so the adapter can reject their use explicitly.
                  // Definitions can contain image URLs; never copy those here.
                  const name = (rule as CSSCounterStyleRule).name;
                  fontRules.push(
                    `@counter-style ${(previewWindow as Window & typeof globalThis).CSS.escape(name)}{system:cyclic;symbols:""}`
                  );
                } else if (rule.type === 5) {
                  const style = (rule as CSSFontFaceRule).style;
                  let embedded = true;
                  replaceCssUrls(style.getPropertyValue('src'), url => {
                    if (!url.toLowerCase().startsWith('data:')) embedded = false;
                    return '';
                  });
                  if (!embedded) continue;
                  // Constructing a detached FontFace normalizes descriptors; it
                  // neither loads the font nor changes the author's FontFaceSet.
                  const cssFamily = style.getPropertyValue('font-family');
                  const family = decodeCssValue(
                    /^['"]/.test(cssFamily) ? cssFamily.slice(1, -1) : cssFamily
                  );
                  const font = new (previewWindow as Window & typeof globalThis).FontFace(family, style.getPropertyValue('src'), {
                    style: style.getPropertyValue('font-style') || 'normal',
                    weight: style.getPropertyValue('font-weight') || 'normal',
                    stretch: style.getPropertyValue('font-stretch') || 'normal',
                    unicodeRange: style.getPropertyValue('unicode-range') || 'U+0-10FFFF',
                    featureSettings: style.getPropertyValue('font-feature-settings') || 'normal',
                    variationSettings:
                      style.getPropertyValue('font-variation-settings') || 'normal',
                  } as FontFaceDescriptors & { variationSettings: string });
                  copiedFonts.add(fontKey(font, family));
                  fontFaceRules.push({ family: normalizedFontFamily(family), css: rule.cssText });
                } else if ('styleSheet' in rule && (rule as CSSImportRule).styleSheet) {
                  const supports = (rule as CSSImportRule & { supportsText?: string }).supportsText;
                  if (!supports || (previewWindow as Window & typeof globalThis).CSS.supports(supports))
                    collectFonts((rule as CSSImportRule).styleSheet!);
                } else if ('cssRules' in rule) visitRules((rule as CSSGroupingRule).cssRules);
              }
            };
            visitRules(rules);
          };
          for (const sheet of [...document.styleSheets, ...(document.adoptedStyleSheets || [])])
            collectFonts(sheet);
          const copyStyle = (target: HTMLElement | SVGElement, style: CSSStyleDeclaration) => {
            noteFontFamilies(style);
            target.style.cssText = 'all: initial !important;';
            for (const property of style) {
              if (!['all', 'content'].includes(property) && !property.startsWith('--'))
                target.style.setProperty(
                  property,
                  copiedCss(style.getPropertyValue(property)),
                  'important'
                );
            }
            target.style.setProperty('transition-property', 'none', 'important');
            target.style.setProperty('animation', 'none', 'important');
          };
          const rulesFor = (node: Element, id: string) => {
            let rules = '';
            for (const pseudo of ['::before', '::after']) {
              const style = previewWindow.getComputedStyle(node, pseudo);
              if (!style.content || style.content === 'none' || style.content === 'normal')
                continue;
              noteFontFamilies(style);
              const declarations = [...style]
                .filter(property => property !== 'all' && !property.startsWith('--'))
                .map(
                  property =>
                    `${property}:${copiedCss(style.getPropertyValue(property))}!important;`
                )
                .join('');
              rules += `[data-attune-capture-node="${id}"]${pseudo}{all:initial!important;${declarations}}`;
            }
            return rules;
          };
          const topLayerSelector = [':modal', ':popover-open']
            .filter(selector => (previewWindow as Window & typeof globalThis).CSS.supports(`selector(${selector})`))
            .join(',');
          const hasVisibleComposedDescendant = (element: Element): boolean => {
            const assigned =
              element.tagName === 'SLOT' ? (element as HTMLSlotElement).assignedElements() : [];
            const children = assigned.length ? assigned : (element.shadowRoot || element).children;
            return [...children].some(child => {
              const style = previewWindow.getComputedStyle(child);
              if (style.display === 'none' || style.opacity === '0') return false;
              return visible(child) || hasVisibleComposedDescendant(child);
            });
          };
          const clone = (node: Node, rules: string[], painted = true): Node | null => {
            if (node.nodeType === 3) return copy.createTextNode(node.textContent || '');
            if (node.nodeType !== 1) return null;
            const original = node as HTMLElement;
            if (topLayerSelector && original.matches(topLayerSelector))
              throw new Error(
                'This view contains an open dialog or popover that cannot be captured.'
              );
            const media = ['IFRAME', 'OBJECT', 'EMBED', 'VIDEO', 'AUDIO'].includes(
              original.tagName
            );
            if (media && painted && visibleMedia(original))
              throw new Error(
                `This view contains ${original.tagName === 'AUDIO' ? 'audio' : 'media'} controls that cannot be captured.`
              );
            if (
              original.closest(overlaySelector) ||
              ['HEAD', 'SCRIPT', 'STYLE', 'LINK', 'META', 'BASE', 'NOSCRIPT', 'SOURCE'].includes(
                original.tagName
              )
            )
              return null;
            const computed = previewWindow.getComputedStyle(original);
            if (computed.display === 'none') return null;
            if (
              painted &&
              visible(original) &&
              original.namespaceURI === 'http://www.w3.org/2000/svg' &&
              original.localName === 'use'
            ) {
              const href = (original as unknown as SVGUseElement).href.baseVal;
              if (href.startsWith('#')) {
                const root = original.getRootNode() as Document | ShadowRoot;
                const definition = root.getElementById(href.slice(1));
                // The renderer serializes each SVG into a separate image. A
                // target in another SVG is lost even if its hidden tree is kept.
                if (definition && !original.closest('svg')?.contains(definition))
                  throw new Error(
                    'This view uses an SVG sprite outside its image that cannot be captured.'
                  );
              }
            }
            painted = painted && computed.opacity !== '0';
            if (painted && computed.visibility !== 'hidden') {
              if (
                !original.shadowRoot &&
                (previewWindow.customElements.get(original.localName) ||
                  previewWindow.customElements.get(original.getAttribute('is') || ''))
              )
                throw new Error(
                  'This custom element has an inaccessible or unknown shadow root and cannot be captured.'
                );
              if (
                original.namespaceURI === 'http://www.w3.org/2000/svg' &&
                original.localName === 'svg' &&
                original.querySelector(
                  'animate,set,animateTransform,animateMotion,animateColor,discard'
                )
              )
                throw new Error('This view contains animated SVG content that cannot be captured.');
              for (const pseudo of ['::first-letter', '::first-line']) {
                const style = previewWindow.getComputedStyle(original, pseudo);
                if (
                  [
                    'color',
                    'font-size',
                    'font-family',
                    'font-weight',
                    'font-style',
                    'font-stretch',
                    'font-feature-settings',
                    'font-variation-settings',
                    'font-variant-caps',
                    'letter-spacing',
                    'word-spacing',
                    'text-transform',
                    'line-height',
                    'text-shadow',
                    'text-emphasis-color',
                    'text-emphasis-style',
                    'text-emphasis-position',
                    'font-kerning',
                    'font-optical-sizing',
                    'font-variant-ligatures',
                    'font-variant-numeric',
                    'font-variant-east-asian',
                    'font-variant-position',
                  ].some(
                    property =>
                      style.getPropertyValue(property) &&
                      style.getPropertyValue(property) !== computed.getPropertyValue(property)
                  ) ||
                  (style.backgroundColor &&
                    style.backgroundColor !== 'rgba(0, 0, 0, 0)' &&
                    style.backgroundColor !== 'transparent') ||
                  (style.backgroundImage && style.backgroundImage !== 'none') ||
                  (style.textDecorationLine && style.textDecorationLine !== 'none') ||
                  (style.verticalAlign && style.verticalAlign !== 'baseline') ||
                  (pseudo === '::first-letter' &&
                    ((style.cssFloat && style.cssFloat !== 'none') ||
                      ['top', 'right', 'bottom', 'left'].some(side =>
                        ['margin-', 'padding-', 'border-'].some(prefix => {
                          const value = style.getPropertyValue(
                            prefix + side + (prefix === 'border-' ? '-width' : '')
                          );
                          return value && value !== '0px';
                        })
                      )))
                )
                  throw new Error(
                    'This view uses styled first-letter or first-line content that cannot be captured.'
                  );
              }
            }
            if (
              painted &&
              computed.visibility !== 'hidden' &&
              computed.display.includes('list-item')
            ) {
              const marker = previewWindow.getComputedStyle(original, '::marker');
              const customized = [
                'color',
                'font-size',
                'font-family',
                'font-weight',
                'font-style',
                'font-stretch',
                'font-feature-settings',
                'font-variation-settings',
                'font-variant-caps',
                'letter-spacing',
                'word-spacing',
                'text-transform',
              ].some(
                property =>
                  marker.getPropertyValue(property) &&
                  marker.getPropertyValue(property) !== computed.getPropertyValue(property)
              );
              if (customized || (marker.content && marker.content !== 'normal'))
                throw new Error('This view uses a custom list marker that cannot be captured.');
            }
            const target =
              media || original.tagName === 'SLOT'
                ? copy.createElement('div')
                : copy.importNode(original, false);
            if (media || original.tagName === 'SLOT')
              for (const attribute of original.attributes)
                target.setAttribute(attribute.name, attribute.value);
            for (const attribute of [...target.attributes]) {
              if (
                /^on/i.test(attribute.name) ||
                (media &&
                  ['src', 'srcset', 'srcdoc', 'poster', 'data', 'background'].includes(
                    attribute.name
                  )) ||
                [
                  'nonce',
                  'background',
                  'is',
                  'autofocus',
                  'data-attune-capture-node',
                  'data-attune-preview-hover',
                  'data-attune-preview-selected',
                ].includes(attribute.name)
              )
                target.removeAttribute(attribute.name);
            }
            const id = String(nextId++);
            target.setAttribute('data-attune-capture-node', id);
            copyStyle(target, computed);
            // Replaced inline media honors width/height; an ordinary inline div
            // does not. Keep its used box without loading or cloning its content.
            if (media && computed.display === 'inline')
              target.style.setProperty('display', 'inline-block', 'important');
            rules.push(rulesFor(original, id));
            if (original.scrollLeft || original.scrollTop)
              scrolls.push({ id, x: original.scrollLeft, y: original.scrollTop });
            if (media) return target;
            if (
              original.isContentEditable &&
              (!painted || !visible(original)) &&
              (!painted || !hasVisibleComposedDescendant(original))
            ) {
              // The used dimensions copied above keep an unpainted editor's
              // layout box without sending its live text to the parent.
              if (computed.display === 'inline') {
                const bounds = original.getBoundingClientRect();
                target.style.setProperty('display', 'inline-block', 'important');
                target.style.setProperty('width', `${bounds.width}px`, 'important');
                target.style.setProperty('height', `${bounds.height}px`, 'important');
              }
              return target;
            }
            if (original.tagName === 'CANVAS') {
              const image = copy.createElement('img');
              for (const attribute of target.attributes)
                image.setAttribute(attribute.name, attribute.value);
              const canvas = original as HTMLCanvasElement;
              const bounds = original.getBoundingClientRect();
              const bounded = hasBoundedObjectFit(canvas, computed, canvas.width, canvas.height);
              if (bounded && computed.objectFit === 'scale-down')
                image.style.setProperty('object-fit', 'contain', 'important');
              image.src =
                painted && visible(original)
                  ? rasterizedImage(canvas, canvas.width, canvas.height, [
                      bounded
                        ? {
                            width: bounds.width * captureScale,
                            height: bounds.height * captureScale,
                            contain: ['contain', 'scale-down'].includes(computed.objectFit),
                            stretch: !computed.objectFit || computed.objectFit === 'fill',
                            pixelated: ['pixelated', 'crisp-edges'].includes(
                              computed.imageRendering
                            ),
                          }
                        : undefined,
                    ])
                  : emptyImage;
              return image;
            }
            if (original.tagName === 'IMG') {
              const image = target as HTMLImageElement;
              const source = original as HTMLImageElement;
              if (
                computed.objectFit === 'scale-down' &&
                hasBoundedObjectFit(source, computed, source.naturalWidth, source.naturalHeight)
              )
                image.style.setProperty('object-fit', 'contain', 'important');
              image.src = visible(original)
                ? capturedUrl(
                    (original as HTMLImageElement).currentSrc || (original as HTMLImageElement).src
                  )
                : emptyImage;
              image.removeAttribute('srcset');
              image.removeAttribute('sizes');
              image.loading = 'eager';
            }
            if (original.tagName === 'INPUT') {
              const input = original as HTMLInputElement;
              if (painted && input.type === 'image' && visible(original))
                throw new Error('This view contains an image input that cannot be captured.');
              if (input.type === 'image') target.removeAttribute('src');
              if (painted && input.type === 'checkbox' && input.indeterminate && visible(original))
                throw new Error('This view contains a mixed checkbox that cannot be captured.');
              if (painted && input.type === 'file' && input.files?.length && visible(original))
                throw new Error(
                  'This view contains a selected file input that cannot be captured.'
                );
              // The parent receives this HTML. Only painted controls need their
              // runtime state; visible passwords retain only their glyph count.
              target.setAttribute(
                'value',
                input.type === 'file' || !painted || !visible(original)
                  ? ''
                  : input.type === 'password'
                    ? '•'.repeat(input.value.length)
                    : input.value
              );
              target.toggleAttribute('checked', painted && visible(original) && input.checked);
            }
            if (
              original.namespaceURI === 'http://www.w3.org/2000/svg' &&
              ['image', 'feImage'].includes(original.localName)
            ) {
              const href = (original as unknown as SVGImageElement).href.baseVal;
              target.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
              target.setAttribute(
                'href',
                href && svgImageMayPaint(original as unknown as SVGImageElement)
                  ? capturedUrl(href)
                  : emptyImage
              );
            }
            if (
              original.tagName === 'SELECT' &&
              painted &&
              visible(original) &&
              !(original as HTMLSelectElement).multiple &&
              (original as HTMLSelectElement).size <= 1 &&
              (original as HTMLSelectElement).selectedIndex === -1
            ) {
              // HTML reparsing selects the first option by default. A hidden,
              // selected blank preserves the cleared dropdown's displayed state.
              const blank = copy.createElement('option');
              blank.hidden = true;
              blank.setAttribute('selected', '');
              target.appendChild(blank);
            }
            if (original.tagName === 'OPTION') {
              const select = original.closest('select');
              target.toggleAttribute(
                'selected',
                painted && !!select && visible(select) && (original as HTMLOptionElement).selected
              );
            }
            if (original.tagName === 'TEXTAREA')
              target.textContent =
                painted && visible(original) ? (original as HTMLTextAreaElement).value : '';
            else if (original.shadowRoot) {
              const root = original.shadowRoot;
              for (const sheet of [...root.styleSheets, ...(root.adoptedStyleSheets || [])])
                collectFonts(sheet);
              // Declarative shadow roots preserve slot distribution without
              // connecting or upgrading any author custom element.
              const template = copy.createElement('template');
              template.setAttribute('shadowrootmode', 'open');
              const shadowRules: string[] = [];
              for (const child of root.childNodes) {
                const copied = clone(child, shadowRules, painted);
                if (copied) template.content.appendChild(copied);
              }
              const style = copy.createElement('style');
              style.textContent = shadowRules.join('').replace(/</g, '\\3c ');
              template.content.prepend(style);
              target.appendChild(template);
            } else {
              // Flatten slot assignments in the inert shadow copy. Synthetic
              // pseudo children must participate in layout, not become unused
              // slot fallback content when assigned nodes exist.
              const assigned =
                original.tagName === 'SLOT' ? (original as HTMLSlotElement).assignedNodes() : [];
              for (const child of assigned.length ? assigned : original.childNodes) {
                const copied = clone(child, rules, painted);
                if (copied) target.appendChild(copied);
              }
            }
            return target;
          };
          const rules: string[] = [];
          const root = clone(document.documentElement, rules) as HTMLElement;
          const fontCounts = new Map<string, number>();
          for (const font of document.fonts) {
            const key = fontKey(font);
            fontCounts.set(key, (fontCounts.get(key) || 0) + 1);
          }
          for (const font of document.fonts) {
            if (
              font.status === 'loaded' &&
              referencedFontFamilies.has(normalizedFontFamily(font.family)) &&
              (fontCounts.get(fontKey(font)) || 0) > 1
            )
              throw new Error('A font in this view has ambiguous sources for screenshot capture.');
            if (
              font.status === 'loaded' &&
              referencedFontFamilies.has(normalizedFontFamily(font.family)) &&
              !copiedFonts.has(fontKey(font))
            )
              throw new Error('A font in this view is not embedded for screenshot capture.');
          }
          const head = copy.createElement('head');
          const style = copy.createElement('style');
          style.textContent = [
            ...fontRules,
            ...fontFaceRules
              .filter(face => referencedFontFamilies.has(face.family))
              .map(face => face.css),
            ...rules,
          ]
            .join('')
            .replace(/</g, '\\3c ');
          head.appendChild(style);
          root.prepend(head);
          const html = '<!doctype html>' + root.outerHTML;
          if (html.length > 16_000_000)
            throw new Error('This view is too large for screenshot capture.');
          const rendered = await new Promise<PreviewSnapshot>((resolve, reject) => {
            const stop = () => {
              previewWindow.removeEventListener('message', receive);
              controller.signal.removeEventListener('abort', abort);
            };
            const abort = () => {
              stop();
              reject(new Error('Preview capture was cancelled.'));
            };
            const receive = (event: MessageEvent) => {
              const result = event.data;
              if (
                event.source !== previewWindow.parent ||
                result?.source !== 'attune-host' ||
                result.type !== 'annotation-render-result' ||
                result.requestId !== requestId
              )
                return;
              stop();
              if (result.error) reject(new Error(String(result.error).slice(0, 300)));
              else if (
                !result.snapshot ||
                typeof result.snapshot.base64Data !== 'string' ||
                result.snapshot.base64Data.length > 700_000
              )
                reject(new Error('The isolated renderer returned an invalid capture.'));
              else resolve(result.snapshot);
            };
            previewWindow.addEventListener('message', receive);
            controller.signal.addEventListener('abort', abort, { once: true });
            previewWindow.parent.postMessage(
              {
                source: 'attune-preview-annotation',
                type: 'annotation-render',
                requestId,
                html,
                viewport,
                scrolls,
              } satisfies PreviewAnnotationRenderRequest,
              '*'
            );
          });
          return { canvas: null, rendered, elements, capturedAt };
        }
        const canvas = await frame.html2canvas!(document.documentElement, {
          width: viewport.width,
          height: viewport.height,
          x: viewport.scrollX,
          y: viewport.scrollY,
          windowWidth: viewport.width,
          windowHeight: viewport.height,
          scrollX: viewport.scrollX,
          scrollY: viewport.scrollY,
          scale: Math.min(1, 1_600 / Math.max(viewport.width, viewport.height)),
          attuneUseCurrentDocument: directCapture,
          attunePreloadedImages: preloadedImages,
          ...(directCapture ? { customIsSameOrigin: () => false } : {}),
          cspNonce: scope.nonce,
          signal: controller.signal,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
          imageTimeout: 10_000,
          removeContainer: true,
          ignoreElements: (element: Element) => Boolean(element.closest(overlaySelector)),
        });
        return { canvas, rendered: null, elements, capturedAt };
      };
      const { canvas, rendered, elements, capturedAt } = await Promise.race([
        work(),
        new Promise<never>((_, reject) => {
          cancelCapture = () => {
            controller.abort();
            reject(new Error('Preview capture was cancelled.'));
          };
          timeout = setTimeout(() => {
            controller.abort();
            reject(
              new Error('Preview capture timed out. Wait for the page to finish loading and retry.')
            );
          }, 20_000);
        }),
      ]);
      if (!active) return;
      if (
        previewWindow.innerWidth !== viewport.width ||
        previewWindow.innerHeight !== viewport.height ||
        previewWindow.scrollX !== viewport.scrollX ||
        previewWindow.scrollY !== viewport.scrollY
      )
        throw new Error('The preview moved during capture. Keep it still and retry.');
      let base64Data = rendered?.base64Data || '';
      for (const quality of [0.9, 0.8, 0.65, 0.5, 0.35]) {
        if (!canvas) break;
        base64Data = canvas.toDataURL('image/jpeg', quality).split(',')[1] || '';
        if (base64Data && base64Data.length <= 700_000) break;
      }
      if (!base64Data || base64Data.length > 700_000)
        throw new Error(
          'This view is too detailed to attach. Resize the preview smaller and retry.'
        );
      previewProtocol.sendMessage({
        source: 'attune-preview-annotation',
        type: 'annotation-result',
        requestId,
        ...(page ? { page } : {}),
        snapshot: {
          base64Data,
          mimeType: 'image/jpeg',
          width: canvas?.width || rendered!.width,
          height: canvas?.height || rendered!.height,
          capturedAt,
          viewport,
          elements,
        },
      });
    } catch (error) {
      sendError(
        requestId,
        error instanceof Error
          ? error.message.slice(0, 300)
          : 'Preview capture failed. Please retry.'
      );
    } finally {
      captureActive = false;
      previewWindow.removeEventListener('message', receiveCancellation);
      controller.abort();
      for (const { element, display, priority } of hiddenOverlays) {
        if (display) element.style.setProperty('display', display, priority);
        else element.style.removeProperty('display');
      }
      for (const { element, name, value } of inspectorAttributes) {
        if (element.isConnected) element.setAttribute(name, value);
      }
      if (timeout) clearTimeout(timeout);
      timeout = undefined;
      cancelCapture = undefined;
      if ((message.pageIndex !== undefined || message.capturePage || message.scroll) && active) {
        previewWindow.scrollTo({
          left: initialScroll.x,
          top: initialScroll.y,
          behavior: 'instant',
        });
      }
      busy = false;
    }
  };
  previewProtocol.addListener('annotation-capture', message => {
    void capture(message);
  });
  frame.__attunePreviewAnnotationCleanup = () => {
    active = false;
    cancelCapture?.();
    if (timeout) clearTimeout(timeout);
    rendererScript?.remove();
  };
}

export function compiledPreviewAnnotationBridgeScript(
  nonce?: string,
  staticImageUrls: string[] = []
): string {
  return `(() => {
    const listeners = new Map();
    const onMessage = event => {
      if (event.source !== window.parent || event.data?.source !== 'attune-host') return;
      for (const listener of listeners.get(event.data.type) || []) listener(event.data);
    };
    window.addEventListener('message', onMessage);
    (${installAttunePreviewAnnotationBridge.toString()})({ previewWindow: window, previewProtocol: {
      addListener(type, listener) { const current = listeners.get(type) || []; current.push(listener); listeners.set(type, current); },
      sendMessage(message) { window.parent.postMessage(message, '*'); }
    }, scope: ${JSON.stringify({ ...(nonce ? { nonce } : {}), staticImageUrls }).replace(/</g, '\\u003c')} });
    const cleanup = window.__attunePreviewAnnotationCleanup;
    window.__attunePreviewAnnotationCleanup = () => { cleanup?.(); window.removeEventListener('message', onMessage); };
  })();`;
}

let rendererSourcePromise: Promise<string> | undefined;
export function loadPreviewAnnotationRenderer(): Promise<string> {
  if (!rendererSourcePromise) {
    rendererSourcePromise = fetch('/vendor/html2canvas-pro-2.4.1.min.js?adapter=2')
      .then(async response => {
        if (!response.ok)
          throw new Error('Could not load the preview screenshot tool. Please retry.');
        const source = await response.text();
        if (!source || source.length > 1_000_000 || !source.includes('html2canvas'))
          throw new Error('The preview screenshot tool is unavailable.');
        return source;
      })
      .catch(error => {
        rendererSourcePromise = undefined;
        throw error;
      });
  }
  return rendererSourcePromise;
}
